import { useState } from 'react'

import { Alert, Button, Checkbox, Group, Modal, Radio, Stack, Text, Title } from '@mantine/core'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'

type SyncChannelVideosModalProps = {
  opened: boolean
  loading: boolean
  onClose: () => void
  onSync: (options: { includeUnlisted: boolean; fullResync: boolean }) => void
}

export const SyncChannelVideosModal = ({
  opened,
  loading,
  onClose,
  onSync,
}: SyncChannelVideosModalProps) => {
  const t = useTranslate()
  const [includeUnlisted, setIncludeUnlisted] = useState(false)
  const [fullResync, setFullResync] = useState(false)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title size="h4">{t('pages.videoPromotion.syncChannelVideos')}</Title>}
    >
      <Stack>
        <Text size="sm">{t('pages.videoPromotion.syncModal.description')}</Text>
        <Radio.Group
          value={fullResync ? 'full' : 'latest'}
          onChange={value => setFullResync(value === 'full')}
        >
          <Stack gap="xs">
            <Radio value="latest" label={t('pages.videoPromotion.syncModal.latestVideos')} />
            <Radio value="full" label={t('pages.videoPromotion.syncModal.fullResync')} />
          </Stack>
        </Radio.Group>
        {fullResync && (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={20} />}>
            {t('pages.videoPromotion.syncModal.fullResyncWarning')}
          </Alert>
        )}
        <Checkbox
          label={t('pages.videoPromotion.syncModal.includeUnlisted')}
          checked={includeUnlisted}
          onChange={event => setIncludeUnlisted(event.currentTarget.checked)}
        />
        {includeUnlisted && (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={20} />}>
            {t('pages.videoPromotion.syncModal.includeUnlistedWarning')}
          </Alert>
        )}
        <Group justify="right" mt="md">
          <Button variant="subtle" onClick={onClose}>
            {t('_generics.cancel')}
          </Button>
          <Button
            leftSection={<IconRefresh size={16} />}
            color={includeUnlisted || fullResync ? 'orange' : undefined}
            loading={loading}
            onClick={() => onSync({ includeUnlisted, fullResync })}
          >
            {t('pages.videoPromotion.syncModal.sync')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
